import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { COLORS } from '../utils/constants';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { userAPI } from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }

      try {
        const response = await userAPI.validateResetToken(token);
        setTokenValid(response.data.valid);
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    
    checkToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setValidating(false);
      return;
    }

    try {
      const response = await userAPI.validateResetToken(token);
      setTokenValid(response.data.valid);
    } catch {
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (password.length < 6) {
      setError('Пароль должен быть не менее 6 символов');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await userAPI.confirmPasswordReset({ token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при сбросе пароля');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: COLORS.textSecondary }}>
          Проверка ссылки...
        </div>
      </div>
    );
  }

  // Invalid or no token
  if (!token || !tokenValid) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '440px',
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${COLORS.danger}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <AlertCircle size={40} color={COLORS.danger} />
          </div>
          
          <h1 style={{ color: COLORS.text, fontSize: '24px', marginBottom: '12px' }}>
            Недействительная ссылка
          </h1>
          
          <p style={{ color: COLORS.textSecondary, marginBottom: '32px', lineHeight: '1.6' }}>
            Ссылка для сброса пароля недействительна или устарела. 
            Пожалуйста, запросите новую ссылку.
          </p>

          <Link
            to="/forgot-password"
            style={{
              display: 'inline-block',
              padding: '14px 28px',
              backgroundColor: COLORS.primary,
              color: '#000',
              textDecoration: 'none',
              borderRadius: '10px',
              fontWeight: '600',
              fontSize: '15px',
              marginBottom: '16px'
            }}
          >
            Запросить новую ссылку
          </Link>

          <div>
            <Link
              to="/login"
              style={{
                color: COLORS.textSecondary,
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              Вернуться к входу
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '440px',
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${COLORS.success}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={40} color={COLORS.success} />
          </div>
          
          <h1 style={{ color: COLORS.text, fontSize: '24px', marginBottom: '12px' }}>
            Пароль изменён
          </h1>
          
          <p style={{ color: COLORS.textSecondary, marginBottom: '32px', lineHeight: '1.6' }}>
            Ваш пароль успешно изменён. Теперь вы можете войти в аккаунт с новым паролем.
          </p>

          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Войти в аккаунт
          </button>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700',
            marginBottom: '8px'
          }}>
            <span style={{ color: COLORS.primary }}>Ex</span>
            <span style={{ color: COLORS.text }}>Dex</span>
          </h1>
        </div>

        {/* Form Card */}
        <div style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '32px'
        }}>
          <h2 style={{ 
            color: COLORS.text, 
            fontSize: '22px', 
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            Создание нового пароля
          </h2>
          
          <p style={{ 
            color: COLORS.textSecondary, 
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            Введите новый пароль для вашего аккаунта
          </p>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              backgroundColor: `${COLORS.danger}15`,
              border: `1px solid ${COLORS.danger}30`,
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <AlertCircle size={18} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* New Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                color: COLORS.textSecondary, 
                fontSize: '14px',
                marginBottom: '8px'
              }}>
                Новый пароль
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={18} 
                  color={COLORS.textSecondary} 
                  style={{ 
                    position: 'absolute', 
                    left: '14px', 
                    top: '50%', 
                    transform: 'translateY(-50%)' 
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Минимум 6 символов"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 44px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    color: COLORS.text,
                    fontSize: '15px',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={18} color={COLORS.textSecondary} />
                  ) : (
                    <Eye size={18} color={COLORS.textSecondary} />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                color: COLORS.textSecondary, 
                fontSize: '14px',
                marginBottom: '8px'
              }}>
                Подтвердите пароль
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={18} 
                  color={COLORS.textSecondary} 
                  style={{ 
                    position: 'absolute', 
                    left: '14px', 
                    top: '50%', 
                    transform: 'translateY(-50%)' 
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите пароль"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 14px 14px 44px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${password && confirmPassword && password !== confirmPassword ? COLORS.danger : COLORS.border}`,
                    borderRadius: '10px',
                    color: COLORS.text,
                    fontSize: '15px',
                    outline: 'none'
                  }}
                />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p style={{ color: COLORS.danger, fontSize: '12px', marginTop: '6px' }}>
                  Пароли не совпадают
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword || password !== confirmPassword}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: loading || !password || !confirmPassword || password !== confirmPassword 
                  ? COLORS.border 
                  : COLORS.primary,
                color: loading || !password || !confirmPassword || password !== confirmPassword 
                  ? COLORS.textSecondary 
                  : '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || !password || !confirmPassword || password !== confirmPassword 
                  ? 'not-allowed' 
                  : 'pointer',
                marginBottom: '20px'
              }}
            >
              {loading ? 'Сохранение...' : 'Сохранить новый пароль'}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: COLORS.textSecondary,
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              <ArrowLeft size={16} />
              Вернуться к входу
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
